import React, { useState } from "react";
import { Modal, Button, Form, Row, Col, Alert } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faDownload, faCalendarAlt, faInfoCircle, faFileExcel, faFilePdf, faArchive } from "@fortawesome/free-solid-svg-icons";

interface FiltrosDescarga {
  desde?: string;
  hasta?: string;
  etapa?: number;
  includeAll?: boolean;
}

interface Props {
  show: boolean;
  onHide: () => void;
  etapaServicio?: number;
  tituloModal?: string;
  tipoDescarga?: "excel" | "pdf";
  onDescargar?: (filtros?: FiltrosDescarga) => void;
}

/**
 * DescargaMasivaModal Component
 * @description: Modal para configurar y ejecutar descargas masivas de reportes Excel o PDFs
 * @date 02/08/2025
 * @param Props - Propiedades del modal
 * @returns JSX del Modal
 */
const DescargaMasivaModal: React.FC<Props> = ({ 
  show, 
  onHide, 
  etapaServicio = 3,
  tituloModal = "Descarga Masiva de Servicios",
  tipoDescarga = "excel",
  onDescargar
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [filtros, setFiltros] = useState({
    desde: "",
    hasta: "",
    includeAll: true
  });

  const handleDescarga = () => {
    if (!onDescargar) return;
    
    setLoading(true);
    const filtrosDescarga = {
      etapa: etapaServicio,
      includeAll: filtros.includeAll,
      desde: filtros.desde,
      hasta: filtros.hasta
    };

    onDescargar(filtrosDescarga);
    
    // Reset loading después de un tiempo para permitir que el hook maneje el estado
    setTimeout(() => setLoading(false), 1000);
  };

  const handleClose = () => {
    if (!loading) {
      onHide();
      // Reset form
      setFiltros({
        desde: "",
        hasta: "",
        includeAll: true
      });
    }
  };

  const isValidDateRange = () => {
    if (!filtros.desde || !filtros.hasta) return false;
    return filtros.desde <= filtros.hasta;
  };

  const getEtapaName = () => {
    switch(etapaServicio) {
      case 1: return "Capturados";
      case 2: return "Programados";
      case 3: return "Atendidos";
      default: return "Servicios";
    }
  };

  const getDescripcionTipo = () => {
    if (tipoDescarga === "excel") {
      return {
        icon: faFileExcel,
        color: "success",
        descripcion: "Se descargará un archivo Excel (.xlsx) con todos los servicios",
        archivo: "Excel con datos tabulares"
      };
    } else {
      return {
        icon: faArchive,
        color: "danger", 
        descripcion: "Se descargarán todos los PDFs individuales comprimidos en un archivo ZIP",
        archivo: "Archivo ZIP con PDFs individuales"
      };
    }
  };

  const tipoInfo = getDescripcionTipo();

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <FontAwesomeIcon icon={tipoInfo.icon as IconProp} className="me-2" />
          {tituloModal}
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        <Alert variant={tipoInfo.color} className="mb-3">
          <FontAwesomeIcon icon={faInfoCircle as IconProp} className="me-2" />
          {tipoInfo.descripcion} {getEtapaName().toLowerCase()} 
          en el rango de fechas seleccionado.
        </Alert>

        {tipoDescarga === "pdf" && (
          <Alert variant="warning" className="mb-3">
            <FontAwesomeIcon icon={faArchive as IconProp} className="me-2" />
            <strong>Nota:</strong> La descarga de PDFs puede tomar varios minutos dependiendo 
            de la cantidad de servicios. Se mostrará el progreso durante la descarga.
          </Alert>
        )}

        <Form>
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>
                  <FontAwesomeIcon icon={faCalendarAlt as IconProp} className="me-1" />
                  Fecha desde <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="date"
                  value={filtros.desde}
                  onChange={(e) => setFiltros(prev => ({ ...prev, desde: e.target.value }))}
                  disabled={loading}
                  required
                />
                <Form.Text className="text-muted">
                  Fecha de inicio del rango a descargar
                </Form.Text>
              </Form.Group>
            </Col>
            
            <Col md={6}>
              <Form.Group>
                <Form.Label>
                  <FontAwesomeIcon icon={faCalendarAlt as IconProp} className="me-1" />
                  Fecha hasta <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="date"
                  value={filtros.hasta}
                  onChange={(e) => setFiltros(prev => ({ ...prev, hasta: e.target.value }))}
                  disabled={loading}
                  required
                />
                <Form.Text className="text-muted">
                  Fecha final del rango a descargar
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          {!isValidDateRange() && (filtros.desde || filtros.hasta) && (
            <Alert variant="danger" className="mb-3">
              {!filtros.desde || !filtros.hasta 
                ? "Ambas fechas son obligatorias"
                : "La fecha 'desde' debe ser anterior o igual a la fecha 'hasta'"
              }
            </Alert>
          )}

          <Row className="mb-3">
            <Col>
              <Form.Check
                type="checkbox"
                id="includeAll"
                label="Incluir todos los servicios (sin paginación)"
                checked={filtros.includeAll}
                onChange={(e) => setFiltros(prev => ({ ...prev, includeAll: e.target.checked }))}
                disabled={loading}
              />
              <Form.Text className="text-muted">
                Recomendado para obtener todos los registros disponibles
              </Form.Text>
            </Col>
          </Row>

          <Alert variant="light" className="mb-0">
            <strong>Tipo de archivo:</strong> {tipoInfo.archivo}
          </Alert>
        </Form>
      </Modal.Body>
      
      <Modal.Footer>
        <Button 
          variant="secondary" 
          onClick={handleClose}
          disabled={loading}
        >
          Cancelar
        </Button>
        <Button 
          variant={tipoInfo.color}
          onClick={handleDescarga}
          disabled={loading || !isValidDateRange()}
        >
          <FontAwesomeIcon icon={faDownload as IconProp} className="me-1" />
          {loading ? "Descargando..." : `Descargar ${tipoDescarga === "excel" ? "Excel" : "PDFs"}`}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DescargaMasivaModal;
